import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    Button,
    CircularProgress,
    Alert,
    Tooltip,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TableSortLabel,
    Snackbar,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate } from "react-router-dom";
import { getAllUsers, runAnalyzerForAllUsers } from "../api/AiAgentApi";

function descendingComparator(a, b, orderBy) {
    const va = a?.[orderBy];
    const vb = b?.[orderBy];
    if (vb === undefined || vb === null) return -1;
    if (va === undefined || va === null) return 1;
    if (vb < va) return -1;
    if (vb > va) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilized = array.map((el, idx) => [el, idx]);
    stabilized.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

export const UsersPage = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");

    const [runningAll, setRunningAll] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "" });

    const fetchData = async () => {
        try {
            setLoading(true);
            setErr(null);
            const res = await getAllUsers();
            setRows(res ?? []);
        } catch (e) {
            setErr(e?.message || "Не удалось загрузить пользователей");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const sortedRows = useMemo(
        () => stableSort(rows, getComparator(order, orderBy)),
        [rows, order, orderBy]
    );

    const runAll = async () => {
        try {
            setRunningAll(true);
            await runAnalyzerForAllUsers();
            alert('Success')
        } catch (e) {
            setSnack({ open: true, msg: e?.message || "Не удалось запустить анализ" });
        } finally {
            setRunningAll(false);
        }
    };

    return (
        <Box sx={{ p: 2, width: "100%" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Tooltip title="Запустить анализ для всех пользователей">
          <span>
            <Button
                variant="contained"
                color="success"
                startIcon={runningAll ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                onClick={runAll}
                disabled={runningAll}
            >
              Запустить анализ для всех
            </Button>
          </span>
                </Tooltip>

                <Button onClick={fetchData} disabled={loading}>
                    {loading ? <CircularProgress size={18} /> : "Обновить"}
                </Button>
            </Stack>

            {err && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {err}
                </Alert>
            )}

            <Paper variant="outlined">
                <TableContainer sx={{ maxHeight: "calc(100vh - 220px)" }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sortDirection={orderBy === "id" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "id"}
                                        direction={orderBy === "id" ? order : "asc"}
                                        onClick={() => handleRequestSort("id")}
                                    >
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === "telegramId" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "telegramId"}
                                        direction={orderBy === "telegramId" ? order : "asc"}
                                        onClick={() => handleRequestSort("telegramId")}
                                    >
                                        Telegram ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={orderBy === "amoCrmContactId" ? order : false}>
                                    <TableSortLabel
                                        active={orderBy === "amoCrmContactId"}
                                        direction={orderBy === "amoCrmContactId" ? order : "asc"}
                                        onClick={() => handleRequestSort("amoCrmContactId")}
                                    >
                                        amoCRM Contact
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">Действия</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={22} />
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        Нет данных
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading &&
                                sortedRows.map((r) => (
                                    <TableRow
                                        key={r.id}
                                        hover
                                        sx={{ cursor: r?.id ? "pointer" : "default" }}
                                        onClick={() => r?.id && navigate(`/users/${r.id}/analyzer`)}
                                    >
                                        <TableCell>{r?.id ?? "—"}</TableCell>
                                        <TableCell>{r?.telegramId ?? "—"}</TableCell>
                                        <TableCell>{r?.amoCrmContactId ?? "—"}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Перейти на анализ пользователя">
                        <span>
                          <Button
                              size="small"
                              variant="contained"
                              startIcon={<AnalyticsIcon />}
                              onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/users/${r.id}/analyzer`);
                              }}
                              disabled={!r?.id}
                          >
                            Анализ
                          </Button>
                        </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                message={snack.msg}
            />
        </Box>
    );
};

export default UsersPage;
